'use client';

import { motion } from 'framer-motion';
import { useLocale } from 'next-intl';
import EmailCtaPanel from '@/components/EmailCtaPanel';

const translations = {
  en: {
    sectionTag: 'Get Started',
    title: 'Let\'s Talk About Your Project',
    subtitle: 'Tell us how AI could automate your business operations. No commitment, just a conversation.',
    benefits: [
      'Identify automation opportunities',
      'Get a custom solution proposal',
      'See real ROI projections',
    ],
  },
  lt: {
    sectionTag: 'Pradėkite',
    title: 'Pakalbėkime Apie Jūsų Projektą',
    subtitle: 'Parašykite, kaip AI galėtų automatizuoti jūsų verslo procesus. Jokių įsipareigojimų, tik pokalbis.',
    benefits: [
      'Nustatyti automatizacijos galimybes',
      'Gauti individualų sprendimo pasiūlymą',
      'Matyti realias ROI prognozes',
    ],
  },
};

export default function BookCallSection() {
  const locale = useLocale();
  const t = translations[locale as keyof typeof translations] || translations.en;

  return (
    // The `book-call` id is the scroll target for the Header, Footer, HeroSection
    // and several service pages. Kept as-is so those handlers keep working.
    <section id="book-call" className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-purple-500/20 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:72px_72px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">
              {t.sectionTag}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              {t.title}
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              {t.subtitle}
            </p>

            {/* Benefits list */}
            <ul className="space-y-4">
              {t.benefits.map((benefit, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center text-gray-200"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center mr-3 flex-shrink-0">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {benefit}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Right side - Email CTA */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <EmailCtaPanel variant="dark" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
